import { isFunction } from '../shared/runtime-values.js';
const stylesheetLoads = new WeakMap<Document, Map<string, Promise<void>>>();
const installedDialogPrototypes = new WeakSet<object>();
const pendingDialogOpens = new WeakSet<object>();

function stylesheetHref(metaName: string, doc: Document) {
  return doc.querySelector<HTMLMetaElement>(`meta[name="${metaName}"]`)?.content || '';
}

export function loadStylesheetFromMeta(metaName: string, doc: Document = document): Promise<void> {
  const href = stylesheetHref(metaName, doc);
  if (!href) return Promise.resolve();

  let loads = stylesheetLoads.get(doc);
  if (!loads) {
    loads = new Map();
    stylesheetLoads.set(doc, loads);
  }
  const existing = loads.get(href);
  if (existing) return existing;

  const linked = Array.from(doc.head.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
    .find(link => link.getAttribute('href') === href);
  if (linked?.sheet) return Promise.resolve();

  const load = new Promise<void>((resolve) => {
    const link = linked ?? doc.createElement('link');
    link.rel = 'stylesheet';
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });
    if (!linked) {
      link.href = href;
      doc.head.appendChild(link);
    }
  });
  loads.set(href, load);
  return load;
}

export function installColdDialogStyles(doc: Document = document) {
  const view = doc.defaultView;
  const prototype = view?.HTMLDialogElement?.prototype;
  if (!prototype || installedDialogPrototypes.has(prototype)) return;
  installedDialogPrototypes.add(prototype);

  const nativeShowModal = isFunction(prototype.showModal)
    ? prototype.showModal
    : null;
  const nativeClose = isFunction(prototype.close)
    ? prototype.close
    : null;

  prototype.showModal = function showModalAfterStyles() {
    const href = stylesheetHref('app-dialog-stylesheet', doc);
    if (!href) {
      if (nativeShowModal) nativeShowModal.call(this);
      else this.setAttribute('open', '');
      return;
    }
    pendingDialogOpens.add(this);
    return loadStylesheetFromMeta('app-dialog-stylesheet', doc).then(() => {
      if (!pendingDialogOpens.delete(this)) return;
      if (this.open) return;
      if (nativeShowModal) nativeShowModal.call(this);
      else this.setAttribute('open', '');
    });
  };

  prototype.close = function closeAndCancelPending(returnValue?: string) {
    pendingDialogOpens.delete(this);
    if (nativeClose) {
      if (returnValue === undefined) nativeClose.call(this);
      else nativeClose.call(this, returnValue);
      return;
    }
    if (returnValue !== undefined) this.returnValue = returnValue;
    this.removeAttribute('open');
  };

  const warmDialogStyles = (event: Event) => {
    const target = event.target instanceof view.Element ? event.target : null;
    if (target?.closest('[data-tooltip], dialog, [role="dialog"]')) {
      void loadStylesheetFromMeta('app-dialog-stylesheet', doc);
    }
  };
  doc.addEventListener('pointerover', warmDialogStyles, { capture: true, passive: true });
  doc.addEventListener('pointerdown', warmDialogStyles, { capture: true, passive: true });
  doc.addEventListener('focusin', warmDialogStyles, { capture: true, passive: true });
}
